import React, { useMemo } from 'react'
import { MediaCategory } from '../../../../types/Media'
import { useTranslation } from 'react-i18next'
import cx from 'classnames'
import styles from './EditCategoryModal.module.scss'
import Button from '../../../../components/Button'
import Input from '../../../../components/Input'
import Select from '../../../../components/Select'
import { useUpdateData } from '../../../../hooks/useUpdateData'
import * as yup from 'yup'
import { treeOptions } from '../../../../helpers/treeOptions'
import { ApiRoutes } from '../../../../services/ApiService'
import { closeModal } from '../../../../services/ModalService'
import { showToast } from '../../../../services/ToastService'

type EditCategoryModalProps = {
    category: MediaCategory
    categories: MediaCategory[]
    onSubmit: () => void
}

const EditCategoryModal: React.FC<EditCategoryModalProps> = ({
    category,
    categories,
    onSubmit,
}) => {
    const { t } = useTranslation(['settings'])
    const editCategorySchema = useMemo(() => {
        const pos_parents = categories
            .filter(
                (cat) => cat.parent_id !== category.id && cat.id !== category.id
            )
            .map((c) => c.id + '')

        pos_parents.push('')

        return {
            name: yup.string().required().min(3),
            parent_id: yup.string().oneOf(pos_parents),
        }
    }, [categories, category])

    const { element, changeProp, isValid, submit } =
        useUpdateData<MediaCategory>(
            ApiRoutes.MEDIA_CATEGORIES,
            category,
            category.id,
            editCategorySchema
        )

    const handleSubmit = () => {
        submit()
            .then((res) => {
                if (res.status === 200) {
                    showToast(t('categories.edit_modal.success'))
                    onSubmit()
                    closeModal()
                } else {
                    showToast(t('categories.edit_modal.success'), 'error')
                }
            })
            .catch(() => {
                showToast(t('categories.edit_modal.success'))
            })
    }

    const selectOptions = useMemo(() => {
        return treeOptions<MediaCategory>(
            categories.filter(
                (cat) => cat.parent_id !== category.id && cat.id !== category.id
            ),
            null,
            'name',
            'id',
            'parent_id',
            'id',
            0
        )
    }, [categories, category])

    return (
        <div className={cx(styles['modal-wrapper'])}>
            <Input
                property={'name'}
                type="text"
                placeholder={t('categories.labels.name')}
                value={element.name}
                onChange={(_, value) => changeProp({ name: value })}
                classes={'zr-w-100 zr-mx-4 zr-my-3'}
            />
            <Select
                options={selectOptions}
                onSelect={(_, value) => changeProp({ parent_id: value })}
                property={'parentCategory'}
                value={element.parent_id + ''}
                placeholder={t('categories.labels.parent')}
                classes={'zr-w-100 zr-mx-4 zr-my-3'}
            />
            <Button
                hierarchy="primary"
                text={t('categories.edit_modal.action')}
                fn={() => handleSubmit()}
                disabled={!isValid}
                classes={'zr-w-100 zr-mx-4 zr-my-2'}
            />
            <Button
                hierarchy="secondary"
                text={t('common.abort')}
                fn={() => closeModal()}
                classes={'zr-w-100 zr-mx-4 zr-my-2'}
            />
        </div>
    )
}

export default EditCategoryModal
